import React, { useEffect, useState, useMemo } from "react";
import useLatestRequest from "../utils/useLatestRequest";
import { Card, Button, Space, Spin, message, Select, DatePicker, Switch, Table } from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import moment from "moment";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import {
  DEFAULT_SUFFIX_OPTIONS,
  DEFAULT_SELECTED_SUFFIXES,
  selectAllDropdownRender,
} from "../utils/suffixFilterUtils";

const thStyle = {
  backgroundColor: "#daeffe",
  fontSize: "12px",
  fontWeight: "bold",
  padding: "8px 4px",
  border: "1px solid #e8e8e8",
  position: "sticky",
  top: 0,
  zIndex: 2,
  boxShadow: "0 1px 0 0 #e8e8e8",
};
const tdStyle = { padding: "6px 8px", border: "1px solid #e8e8e8", fontSize: "12px", color: "#333" };

const { RangePicker } = DatePicker;
const { Option } = Select;

const formatCurrency = (amount) => {
  if (amount === "" || amount == null) return "-";
  const n = Number(amount);
  if (!n) return "-";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
};

/** Root grand totals row — show 0 and decimals from API */
const formatGrandTotalAmount = (v) => {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return "";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

const PropertyAndMediaWisePurchaseReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [uniquePurchasePartyNames, setUniquePurchasePartyNames] = useState([]);
  const [uniquePartyName, setUniquePartyName] = useState([]);
  const [mediaPlanners, setMediaPlanners] = useState([]);
  const [selectedPurchasePartyName, setSelectedPurchasePartyName] = useState(undefined);
  const [selectedPartyName, setSelectedPartyName] = useState(undefined);
  const [selectedMediaPlanners, setSelectedMediaPlanners] = useState(undefined);
  const [selectedSuffix, setSelectedSuffix] = useState(DEFAULT_SELECTED_SUFFIXES);
  const [dateRange, setDateRange] = useState(null);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [isSummary, setIsSummary] = useState(false);
  const [summaryTotal, setSummaryTotal] = useState(null);
  const [grandTotals, setGrandTotals] = useState(null);

  const reportQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("financialYear", financialYear);
    params.set("isSummary", isSummary ? "true" : "false");
    if (selectedPurchasePartyName)
      params.set("uniquePurchasePartyName", selectedPurchasePartyName);
    if (selectedPartyName) params.set("uniquePartyName", selectedPartyName);
    if (selectedMediaPlanners) params.set("mediaPlanners", selectedMediaPlanners);
    if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0)
      params.set("suffix", selectedSuffix.join(","));
    if (dateRange && dateRange[0])
      params.set("startDate", dateRange[0].format("DD-MM-YYYY"));
    if (dateRange && dateRange[1])
      params.set("endDate", dateRange[1].format("DD-MM-YYYY"));
    return params.toString();
  }, [
    financialYear,
    isSummary,
    selectedPurchasePartyName,
    selectedPartyName,
    selectedMediaPlanners,
    selectedSuffix,
    dateRange,
  ]);

  const beginRequest = useLatestRequest();

  useEffect(() => {
    const isCurrent = beginRequest();
    const fetchData = async () => {
      setLoading(true);
      setSummaryTotal(null);
      setGrandTotals(null);
      try {
        const url = `${apiBaseUrl}/inventoryManagement/getPropertyAndMediaWisePurchaseReport${reportQueryParams ? `?${reportQueryParams}` : ""}`;
        const res = await fetch(url, { method: "GET", credentials: "include" });
        const j = await res.json();
        if (!isCurrent()) return;
        if (j.success) {
          setData(j.data || []);
          setGrandTotals(j.grandTotals ?? null);
          if (isSummary && j.total != null) setSummaryTotal(j.total);
          if (!isSummary) {
            if (j.uniquePurchasePartyName)
              setUniquePurchasePartyNames(
                Array.isArray(j.uniquePurchasePartyName) ? j.uniquePurchasePartyName : []
              );
            if (j.uniquePartyName) setUniquePartyName(j.uniquePartyName || []);
            if (j.mediaPlanners) setMediaPlanners(j.mediaPlanners || []);
          }
        } else {
          message.error(j.message || "Failed to fetch report");
          setGrandTotals(null);
        }
      } catch (err) {
        if (!isCurrent()) return;
        console.error("Error fetching Property & Media-Wise Purchase report:", err);
        message.error("Error fetching report");
        setGrandTotals(null);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };
    fetchData();
  }, [apiBaseUrl, reportQueryParams, isSummary]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      params.set("isExcel", "true");
      params.set("isSummary", isSummary ? "true" : "false");
      if (selectedPurchasePartyName)
        params.set("uniquePurchasePartyName", selectedPurchasePartyName);
      if (selectedPartyName) params.set("uniquePartyName", selectedPartyName);
      if (selectedMediaPlanners) params.set("mediaPlanners", selectedMediaPlanners);
      if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0)
        params.set("suffix", selectedSuffix.join(","));
      if (dateRange?.[0]) params.set("startDate", dateRange[0].format("DD-MM-YYYY"));
      if (dateRange?.[1]) params.set("endDate", dateRange[1].format("DD-MM-YYYY"));
      const url = `${apiBaseUrl}/inventoryManagement/getPropertyAndMediaWisePurchaseReport?${params.toString()}`;
      const res = await fetch(url, { method: "GET", credentials: "include" });
      if (!res.ok) {
        message.error("Failed to download Excel");
        return;
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = isSummary
        ? `Property_Media_Wise_Purchase_Summary_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`
        : `Property_Media_Wise_Purchase_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Error downloading Excel:", err);
      message.error("Error downloading Excel");
    } finally {
      setDownloading(false);
    }
  };

  const purchasePartyNameOptions = (uniquePurchasePartyNames || []).map((p) => ({
    label: p,
    value: p,
  }));
  const salesPartyNameOptions = (uniquePartyName || []).map((p) => ({ label: p, value: p }));
  const mediaPlannerOptions = (mediaPlanners || []).map((m) => ({ label: m?.name ?? m, value: m?.name ?? m }));

  const list = data || [];

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "20px",
        minHeight: "100vh",
      }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              Property & Media-Wise Purchase
            </Space>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                padding: "12px 0",
                marginBottom: "4px",
              }}
            >
              <Space wrap size="middle">
                <Switch
                  checkedChildren="Summary"
                  unCheckedChildren="Detailed"
                  checked={isSummary}
                  onChange={setIsSummary}
                />
                <Select
                  value={financialYear}
                  onChange={setFinancialYear}
                  style={{ width: 120 }}
                  placeholder="Select FY"
                >
                  {getFinancialYearOptions().map((fy) => (
                    <Option key={fy} value={fy}>
                      {fy}
                    </Option>
                  ))}
                </Select>
                <Select
                  placeholder="Filter by Purchase Party Name"
                  options={purchasePartyNameOptions}
                  value={selectedPurchasePartyName}
                  onChange={setSelectedPurchasePartyName}
                  style={{ minWidth: 280 }}
                  allowClear
                  showSearch
                  filterOption={(input, opt) => {
                    const label = String(opt?.label ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                />
                <Select
                  placeholder="Filter by Sales Party Name"
                  options={salesPartyNameOptions}
                  value={selectedPartyName}
                  onChange={setSelectedPartyName}
                  style={{ minWidth: 280 }}
                  allowClear
                  showSearch
                  filterOption={(input, opt) => {
                    const label = String(opt?.label ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                />
              </Space>
              <Space wrap size="middle">
                <Select
                  mode="multiple"
                  placeholder="Filter by Suffix"
                  options={DEFAULT_SUFFIX_OPTIONS}
                  value={selectedSuffix.length ? selectedSuffix : undefined}
                  onChange={(v) => setSelectedSuffix(v ?? [])}
                  style={{ minWidth: 160 }}
                  allowClear
                  dropdownRender={selectAllDropdownRender(
                    DEFAULT_SUFFIX_OPTIONS,
                    selectedSuffix,
                    setSelectedSuffix,
                  )}
                />
                <Select
                  placeholder="Filter by Media Planner"
                  options={mediaPlannerOptions}
                  value={selectedMediaPlanners}
                  onChange={setSelectedMediaPlanners}
                  style={{ minWidth: 240 }}
                  allowClear
                  showSearch
                  filterOption={(input, opt) => {
                    const label = String(opt?.label ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                />
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="DD-MM-YYYY"
                  style={{ width: 280 }}
                  allowClear
                />
                <Button
                  type="primary"
                  icon={<FaDownload />}
                  loading={downloading}
                  onClick={handleDownloadExcel}
                >
                  Download Excel
                </Button>
              </Space>
            </div>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : list.length === 0 && !grandTotals ? (
          <div style={{ textAlign: "center", padding: 60, color: "#999" }}>No records found</div>
        ) : isSummary ? (
          <Table
            columns={[
              {
                title: "Purchase Party Name",
                dataIndex: "purchasePartyName",
                key: "purchasePartyName",
                width: 280,
                align: "center",
              },
              {
                title: "Purchase Amount",
                dataIndex: "purchaseAmount",
                key: "purchaseAmount",
                width: 140,
                align: "center",
                render: (_, row) => formatCurrency(row.purchaseAmount ?? row.amount),
              },
            ]}
            dataSource={list}
            rowKey={(r, i) => r.purchasePartyName ?? `summary-${i}`}
            pagination={false}
            size="small"
            components={{
              header: {
                cell: (props) => <th {...props} style={{ ...props.style, backgroundColor: "#daeffe" }} />,
              },
            }}
            summary={() => {
              const amt =
                grandTotals?.purchaseAmount != null && grandTotals.purchaseAmount !== ""
                  ? grandTotals.purchaseAmount
                  : grandTotals?.amount != null && grandTotals.amount !== ""
                    ? grandTotals.amount
                    : summaryTotal;
              return amt != null && amt !== "" ? (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} align="center">
                      <strong>Grand Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="center">
                      <strong>{formatGrandTotalAmount(amt)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null;
            }}
          />
        ) : (
          <div style={{ overflow: "auto", maxHeight: 600 }}>
            <table
              style={{
                width: "100%",
                minWidth: 2030,
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 110, textAlign: "center" }}>PO No</th>
                  <th style={{ ...thStyle, width: 100, textAlign: "center" }}>Date</th>
                  <th style={{ ...thStyle, width: 180, textAlign: "left" }}>Purchase Party Name</th>
                  <th style={{ ...thStyle, width: 220, textAlign: "left" }}>Location</th>
                  <th style={{ ...thStyle, width: 100, textAlign: "center" }}>Booking Days</th>
                  <th style={{ ...thStyle, width: 100, textAlign: "center" }}>From Date</th>
                  <th style={{ ...thStyle, width: 100, textAlign: "center" }}>To Date</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "right" }}>Purchase Amount</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "left" }}>Salesman</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "left" }}>Media Planner</th>
                  <th style={{ ...thStyle, width: 180, textAlign: "left" }}>Sales Party Name</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "left" }}>Brand Segment</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "left" }}>Brand Name</th>
                  <th style={{ ...thStyle, width: 70, textAlign: "center" }}>Suffix</th>
                  <th style={{ ...thStyle, width: 90, textAlign: "center" }}>Category</th>
                  <th style={{ ...thStyle, width: 70, textAlign: "center" }}>Width</th>
                  <th style={{ ...thStyle, width: 70, textAlign: "center" }}>Height</th>
                </tr>
              </thead>
              <tbody>
                {list.map((row, idx) => (
                  <tr key={`${row.poNo || ""}-${row.fromDate || ""}-${idx}`}>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.poNo ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.date ? moment(row.date).format("DD-MM-YYYY") : "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.purchasePartyName ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.location ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.bookingDays ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.fromDate ? moment(row.fromDate).format("DD-MM-YYYY") : "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.toDate ? moment(row.toDate).format("DD-MM-YYYY") : "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{formatCurrency(row.purchaseAmount)}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.salesman ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.mediaPlanner ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.salesPartyName ?? row.partyName ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.brandSegment ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.brandName ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.suffix ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.category ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.width ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.height ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
              {grandTotals && (
                <tfoot>
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        ...tdStyle,
                        textAlign: "center",
                        fontWeight: "bold",
                        backgroundColor: "#f0f9ff",
                      }}
                    >
                      Grand Total
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: "right",
                        fontWeight: "bold",
                        backgroundColor: "#f0f9ff",
                      }}
                    >
                      {formatGrandTotalAmount(
                        grandTotals.purchaseAmount != null && grandTotals.purchaseAmount !== ""
                          ? grandTotals.purchaseAmount
                          : grandTotals.amount
                      )}
                    </td>
                    <td colSpan={9} style={{ ...tdStyle, backgroundColor: "#f0f9ff" }} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PropertyAndMediaWisePurchaseReport;
